/*
 * PD-15699 — BLE transport for Meskernel merchandiser sensor units.
 *
 * Drives the confirmed configuration profile (see
 * docs/claude/sensor-ble-config-handoff.md): identity on `fff00100`, the
 * settings block on `fff00105`, and the single writable command channel
 * `fff00200`. All protocol parsing/building is the pure codec in
 * libs/core/src/sensor-ble; this service only moves bytes.
 *
 * Modeled on ReceiptPrinterService (the proven BLE pattern in this repo):
 * singleton + subscribe observer, broad `requestLEScan` with name matching in
 * the callback, dynamic service resolution on the live connection. Two
 * platform modes (spec.md §6.1):
 *
 *   - Native app: silent multi-device fleet scan via `requestLEScan` —
 *     walk the warehouse, every unit appears.
 *   - Desktop Chrome/Edge: `supportsFleetScan()` is false; the UI offers
 *     `pickUnit()` instead, which opens the browser's one-device chooser
 *     (`requestDevice`). Everything after discovery is identical.
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import {
	buildSensorAction,
	buildSensorCommand,
	classifySensorEcho,
	encodeSensorCommand,
	getSensorCommandDef,
	isSensorDevice,
	parseSensorAdvertisedName,
	parseSensorIdentity,
	parseSensorRanging,
	parseSensorSettings,
	parseSensorStatus,
	Platform,
	type SensorCommandCode,
	type SensorEchoClassification,
	type SensorIdentityModel,
	type SensorRangingModel,
	type SensorSettingsModel,
	type SensorStatusModel,
} from '@supply-mover-2-0/core';
import {
	MockSensorConfigService,
	readMockFleetConfig,
} from './SensorConfigMock';

/**
 * All configuration characteristics share this UUID suffix; only the first
 * 32 bits vary (confirmed by enumerating a live unit).
 */
const SENSOR_UUID_SUFFIX = '-2540-0091-5101-00350618775a';

export const SENSOR_CHARACTERISTICS = {
	/** `mfn,dn,sn,hwv,fwv` — the serial (`sn`) is the DB `deviceId` key. */
	identity: `fff00100${SENSOR_UUID_SUFFIX}`,
	/** Cell/battery status (`power,chg,vbat,tmpr,net,reg,csq,ss,...`). */
	status: `fff00101${SENSOR_UUID_SUFFIX}`,
	/** Per-module ranging lists: depth `d`, signal `s`, temperature `t`. */
	ranging: `fff00103${SENSOR_UUID_SUFFIX}`,
	/** The device settings block (`url,api_key,apn_name,LD_n,upp,...`). */
	settings: `fff00105${SENSOR_UUID_SUFFIX}`,
	/** Command channel — the only writable characteristic. */
	command: `fff00200${SENSOR_UUID_SUFFIX}`,
} as const;

/**
 * Web Bluetooth only exposes services declared up front, and the unit's
 * primary-service UUID is not documented — the bench tool proved access works
 * by declaring the known configuration UUIDs plus the fff0 base service, so
 * we declare the same set and resolve the real containing service from the
 * live GATT table after connecting.
 */
const SENSOR_OPTIONAL_SERVICES: ReadonlyArray<string> = [
	`fff00100${SENSOR_UUID_SUFFIX}`,
	`fff00101${SENSOR_UUID_SUFFIX}`,
	`fff00102${SENSOR_UUID_SUFFIX}`,
	`fff00103${SENSOR_UUID_SUFFIX}`,
	`fff00104${SENSOR_UUID_SUFFIX}`,
	`fff00105${SENSOR_UUID_SUFFIX}`,
	`fff00200${SENSOR_UUID_SUFFIX}`,
	'0000fff0-0000-1000-8000-00805f9b34fb',
];

/** A unit surfaced by discovery (fleet scan or the web picker). */
export interface DiscoveredSensor {
	/**
	 * The OS peripheral identifier used for connect/read/write. NOT the
	 * serial — iOS generates it per phone (see ReceiptPrinterService), so it
	 * must never be persisted or sent to the server.
	 */
	deviceId: string;
	/** Advertised name (e.g. `MotorCityICE_A7D3E7EC`). */
	name: string;
	/** The hex code following the prefix in the advertised name. */
	code: string | null;
	/** Signal strength at discovery time, when the platform reports it. */
	rssi: number | null;
}

/** Everything read from one unit in a single connect. */
export interface SensorUnitReadModel {
	deviceId: string;
	identity: SensorIdentityModel;
	settings: SensorSettingsModel;
	/**
	 * Cell/battery status. Null when the block could not be read — it is
	 * diagnostic, so a failure here must not fail the whole read.
	 */
	status: SensorStatusModel | null;
	/**
	 * PD-16068 — per-module depth, signal and temperature from the ranging
	 * block. Null when the block could not be read; like status it is
	 * informational, so a failure here must not fail the whole read.
	 */
	ranging: SensorRangingModel | null;
	/** When this read completed, so the screen can say how old readings are. */
	readAt: Date;
}

export type SensorBleErrorCode =
	| 'bluetooth-unavailable'
	| 'scan-unavailable'
	| 'not-a-sensor'
	| 'unreachable'
	| 'unsupported-device'
	| 'read-failed'
	| 'write-failed';

export class SensorBleError extends Error {
	public readonly code: SensorBleErrorCode;

	public constructor(code: SensorBleErrorCode, message: string) {
		super(message);
		this.name = 'SensorBleError';
		this.code = code;
	}
}

export interface SensorScanState {
	scanning: boolean;
	/** Device id of the unit currently connected, if any. */
	connectedDeviceId: string | null;
}

/** Resolved GATT locations of the three configuration characteristics. */
interface ResolvedSensorProfile {
	identityService: string;
	settingsService: string;
	commandService: string;
	/** Null when the unit does not expose the status block. */
	statusService: string | null;
	/** Null when the unit does not expose the ranging block. */
	rangingService: string | null;
}

/**
 * The slice of the Web Bluetooth entry point this service touches, typed here
 * rather than pulling the DOM Bluetooth lib into a shared library. `filters` is
 * optional because the plugin omits it when it has nothing to filter on.
 */
interface WebBluetoothLike {
	requestDevice: (options: {
		filters?: Array<{ namePrefix: string }>;
		optionalServices?: Array<string>;
		acceptAllDevices?: boolean;
	}) => Promise<{ deviceId?: string; id?: string; name?: string }>;
}

const sleep = (ms: number): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

/**
 * How long to wait between writing a command and reading the `fff00200` echo.
 * The device result (`:id:code:success`) is the immediate ACK; this small
 * pause lets the firmware replace our written bytes with it.
 */
const ECHO_READ_DELAY_MS = 300;

/**
 * Second-chance echo read for commands whose first read still shows our own
 * bytes — observed on-site with the addressing action, which acknowledges
 * slower than plain settings writes.
 */
const ECHO_RETRY_DELAY_MS = 1200;

/**
 * Web only: pause between gatt.connect() resolving and service discovery.
 * Immediately after a reconnect Chromium can report success while the link
 * is still settling; discovering services in that window throws "GATT
 * Server is disconnected" (observed on-site during a settings write).
 */
const WEB_CONNECT_SETTLE_MS = 500;

/**
 * `fff00105` refreshes ~1–2s AFTER the command ACK (confirmed on hardware) —
 * callers re-reading settings to confirm a write must wait at least this long.
 * Exposed so the UI and tests share one number.
 */
export const SETTINGS_SETTLE_MS = 2000;

/**
 * PD-15776 — hard ceiling on any single BLE operation. GATT connects, reads
 * and writes can hang indefinitely when a link degrades mid-operation (seen
 * in the field: a re-commission command left its button on "Sending…"
 * forever because the underlying write promise never settled, and the whole
 * card stayed locked). Every public operation races this ceiling so a hung
 * radio surfaces as a normal, retryable failure instead of a frozen screen.
 * Generous on purpose: a full unit read walks several characteristics and
 * legitimate operations finish well inside it.
 */
export const BLE_OPERATION_TIMEOUT_MS = 45_000;

/**
 * Race a BLE operation against `BLE_OPERATION_TIMEOUT_MS`. On timeout the
 * promise rejects with a retryable SensorBleError; the underlying operation
 * is left to settle in the background (BLE has no portable cancel), which is
 * harmless — the next operation re-establishes its own link state.
 */
export async function withBleTimeout<T>(
	operation: Promise<T>,
	code: SensorBleErrorCode,
	label: string,
	timeoutMs: number = BLE_OPERATION_TIMEOUT_MS,
): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		return await Promise.race([
			operation,
			new Promise<never>((_resolve, reject) => {
				timer = setTimeout(() => {
					reject(
						new SensorBleError(
							code,
							`${label} timed out after ${Math.round(timeoutMs / 1000)}s — the unit stopped responding. Move closer and try again.`,
						),
					);
				}, timeoutMs);
			}),
		]);
	} finally {
		clearTimeout(timer);
	}
}

/**
 * Singleton BLE transport for sensor units.
 *
 * A singleton because the radio is a device-level resource (same rationale as
 * ReceiptPrinterService); components observe via `subscribe`. Connections are
 * sequential by design — BLE connects one peripheral at a time, and the bulk
 * "read all" flow iterates connect → read → disconnect per unit.
 */
class SensorConfigServiceImpl {
	private _initialized = false;
	private _scanning = false;
	private _connectedDeviceId: string | null = null;
	private _profile: ResolvedSensorProfile | null = null;
	private readonly _listeners = new Set<(state: SensorScanState) => void>();

	public get state(): SensorScanState {
		return {
			scanning: this._scanning,
			connectedDeviceId: this._connectedDeviceId,
		};
	}

	public subscribe(listener: (state: SensorScanState) => void): () => void {
		this._listeners.add(listener);
		return () => {
			this._listeners.delete(listener);
		};
	}

	private _emit(): void {
		const state = this.state;
		for (const listener of this._listeners) {
			listener(state);
		}
	}

	/**
	 * Whether silent multi-device scanning is available. True in the native
	 * app; false on the web.
	 *
	 * Chromium can in fact scan (`requestLEScan`) once experimental web
	 * platform features are enabled, and feature-detecting that was tried —
	 * but it makes the screen behave differently on one laptop than another
	 * for a reason no operator can be expected to know about. The web path
	 * stays deliberately on the one behaviour every browser has: `pickUnit`
	 * and the browser's device chooser. Chooser-free discovery is what the
	 * native app is for.
	 */
	public supportsFleetScan(): boolean {
		return Platform.isNative();
	}

	/**
	 * Whether ANY Bluetooth path exists. On the web this requires a
	 * Web-Bluetooth-capable browser (Chrome/Edge — not Safari/Firefox).
	 */
	public isBluetoothSupported(): boolean {
		if (Platform.isNative()) {
			return true;
		}
		return (
			typeof navigator !== 'undefined' &&
			(navigator as { bluetooth?: unknown }).bluetooth != null
		);
	}

	private async _initialize(): Promise<void> {
		if (this._initialized) {
			return;
		}
		try {
			await BleClient.initialize({ androidNeverForLocation: true });
			this._initialized = true;
		} catch (error) {
			throw new SensorBleError(
				'bluetooth-unavailable',
				error instanceof Error ? error.message : 'Bluetooth is unavailable.',
			);
		}
	}

	// -----------------------------------------------------------------------
	// Discovery
	// -----------------------------------------------------------------------

	/**
	 * Fleet scan (native only): surface every nearby unit whose advertised
	 * name matches one of the tenant's discovery prefixes
	 * (`sensors.discovery.namePatterns`).
	 *
	 * Broad scan with name matching in the callback rather than a service
	 * filter — iOS only reports services present in the ADVERTISING packet,
	 * and (as with the receipt printers) the configuration service is not
	 * advertised.
	 */
	public async scan(
		prefixes: ReadonlyArray<string>,
		onDevice: (device: DiscoveredSensor) => void,
		durationMs = 8000,
	): Promise<void> {
		if (!this.supportsFleetScan()) {
			throw new SensorBleError(
				'scan-unavailable',
				'Fleet scanning is only available in the mobile app. Use the device picker instead.',
			);
		}
		await this._initialize();
		const seen = new Set<string>();
		this._scanning = true;
		this._emit();
		// PD-15740 — units the OS still holds a connection to do NOT advertise,
		// so a pure scan cannot see them. That includes links left open by app
		// versions that never disconnected on Scan. Enumerate held connections
		// first: any that pass the name filter are surfaced like scan hits and
		// their links dropped, so they re-advertise and start over at Connect.
		try {
			const held = await BleClient.getConnectedDevices([
				...SENSOR_OPTIONAL_SERVICES,
			]);
			for (const device of held) {
				const name = device.name;
				if (seen.has(device.deviceId) || !isSensorDevice(name, prefixes)) {
					continue;
				}
				seen.add(device.deviceId);
				onDevice({
					deviceId: device.deviceId,
					name: name ?? '',
					code: parseSensorAdvertisedName(name, prefixes)?.code ?? null,
					rssi: null,
				});
				await this._safeDisconnect(device.deviceId);
			}
		} catch {
			// Enumeration unsupported or failed — the scan below still runs.
		}
		try {
			await BleClient.requestLEScan({ allowDuplicates: false }, (result) => {
				const name = result.device.name ?? result.localName;
				if (
					seen.has(result.device.deviceId) ||
					!isSensorDevice(name, prefixes)
				) {
					return;
				}
				seen.add(result.device.deviceId);
				onDevice({
					deviceId: result.device.deviceId,
					name: name ?? '',
					code: parseSensorAdvertisedName(name, prefixes)?.code ?? null,
					rssi: result.rssi ?? null,
				});
			});
			await sleep(durationMs);
		} finally {
			await this.stopScan();
		}
	}

	/**
	 * NATIVE-ONLY: silently re-find a unit by its advertised identity
	 * (PD-15730) — used after a reboot, where the held peripheral handle is
	 * dead. The scan refreshes the OS's knowledge of the (re-advertising)
	 * unit and returns a FRESH handle; iOS can mint a new peripheral id after
	 * a reboot, so the caller must adopt the returned deviceId rather than
	 * assume it is unchanged. Matches by the advertised code when known,
	 * else by the full advertised name.
	 *
	 * Returns null when the unit is not seen before the timeout, or on web
	 * (where only the user-gesture device picker can refresh a handle — Web
	 * Bluetooth exposes no silent rediscovery).
	 */
	public async rediscoverUnit(
		unit: { name: string; code: string | null },
		prefixes: ReadonlyArray<string>,
		timeoutMs = 90_000,
	): Promise<DiscoveredSensor | null> {
		if (!this.supportsFleetScan()) {
			return null;
		}
		await this._initialize();
		this._scanning = true;
		this._emit();
		return await new Promise<DiscoveredSensor | null>((resolve) => {
			let settled = false;
			const finish = (value: DiscoveredSensor | null): void => {
				if (settled) {
					return;
				}
				settled = true;
				void this.stopScan().then(() => {
					resolve(value);
				});
			};
			const timer = setTimeout(() => {
				finish(null);
			}, timeoutMs);
			BleClient.requestLEScan({ allowDuplicates: false }, (result) => {
				const name = result.device.name ?? result.localName;
				if (!isSensorDevice(name, prefixes)) {
					return;
				}
				const code = parseSensorAdvertisedName(name, prefixes)?.code ?? null;
				const matches =
					unit.code != null ? code === unit.code : name === unit.name;
				if (matches) {
					clearTimeout(timer);
					finish({
						deviceId: result.device.deviceId,
						name: name ?? '',
						code,
						rssi: result.rssi ?? null,
					});
				}
			}).catch(() => {
				clearTimeout(timer);
				finish(null);
			});
		});
	}

	/** Stop an in-flight scan early. Safe when nothing is scanning. */
	public async stopScan(): Promise<void> {
		try {
			await BleClient.stopLEScan();
		} catch {
			// Not scanning — fine.
		}
		if (this._scanning) {
			this._scanning = false;
			this._emit();
		}
	}

	/**
	 * Web fallback: open the browser's one-device chooser and return the
	 * chosen unit. With a single prefix the chooser is pre-filtered; with
	 * several, the plugin's option shape only carries one prefix, so we let
	 * the chooser show everything nearby and validate the pick against the
	 * prefix list afterwards.
	 *
	 * Returns `null` when the user dismisses the chooser — that is a normal
	 * outcome, not an error.
	 */
	public async pickUnit(
		prefixes: ReadonlyArray<string>,
	): Promise<DiscoveredSensor | null> {
		await this._initialize();
		let device: { deviceId: string; name?: string } | null = null;
		try {
			if (
				!Platform.isNative() &&
				prefixes.length > 1 &&
				this._webBluetooth() != null
			) {
				// The plugin's option shape carries ONE namePrefix, but a tenant
				// can have several configured, and dropping the filter makes the
				// chooser list every Bluetooth device in the warehouse. Web
				// Bluetooth itself accepts an ARRAY of filters, so widen the
				// filters on the way through instead of calling requestDevice
				// ourselves: the plugin has to be the one that opens the chooser,
				// because that call is also what registers the granted peripheral
				// in its in-memory device map. Choosing the device directly leaves
				// the plugin unaware of it and every later connect() fails with
				// "Device not found" — unrecoverably, since the getDevices()
				// repair path in _ensureDeviceKnown depends on
				// navigator.bluetooth.getDevices(), which only exists behind a
				// Chromium flag.
				const chosen = await this._withWidenedChooserFilters(
					prefixes,
					async () =>
						await BleClient.requestDevice({
							namePrefix: prefixes[0],
							optionalServices: [...SENSOR_OPTIONAL_SERVICES],
						}),
				);
				device = { deviceId: chosen.deviceId, name: chosen.name };
			} else {
				const chosen = await BleClient.requestDevice({
					namePrefix: prefixes.length === 1 ? prefixes[0] : undefined,
					optionalServices: [...SENSOR_OPTIONAL_SERVICES],
				});
				device = { deviceId: chosen.deviceId, name: chosen.name };
			}
		} catch (error) {
			// The chooser throws when dismissed; treat cancellation as "no pick".
			if (
				error instanceof Error &&
				/cancel|not found|no devices/i.test(error.message)
			) {
				return null;
			}
			throw new SensorBleError(
				'bluetooth-unavailable',
				error instanceof Error
					? error.message
					: 'Could not open the device picker.',
			);
		}
		if (!isSensorDevice(device.name, prefixes)) {
			throw new SensorBleError(
				'not-a-sensor',
				'The selected device is not a recognized sensor unit.',
			);
		}
		return {
			deviceId: device.deviceId,
			name: device.name ?? '',
			code: parseSensorAdvertisedName(device.name, prefixes)?.code ?? null,
			rssi: null,
		};
	}

	/**
	 * The browser's Bluetooth entry point, when this runtime has one. Typed
	 * narrowly here rather than pulling in DOM Bluetooth lib types.
	 */
	private _webBluetooth(): WebBluetoothLike | null {
		if (typeof navigator === 'undefined') {
			return null;
		}
		const bluetooth = (navigator as { bluetooth?: WebBluetoothLike }).bluetooth;
		return bluetooth ?? null;
	}

	/**
	 * Run a plugin call that opens the browser device chooser, with the
	 * chooser's filter list widened to one `namePrefix` filter per configured
	 * prefix.
	 *
	 * The plugin can only express a single prefix, and we need the plugin to
	 * make the call (see `pickUnit`), so the widening happens at the one place
	 * both agree on: `navigator.bluetooth.requestDevice`. The override is
	 * installed for the duration of the call and always restored.
	 */
	private async _withWidenedChooserFilters<T>(
		prefixes: ReadonlyArray<string>,
		run: () => Promise<T>,
	): Promise<T> {
		const bluetooth = this._webBluetooth();
		if (bluetooth == null) {
			return await run();
		}
		const original = bluetooth.requestDevice.bind(bluetooth);
		bluetooth.requestDevice = async (
			options,
		): Promise<{ deviceId?: string; id?: string; name?: string }> =>
			await original({
				...options,
				filters: prefixes.map((namePrefix): { namePrefix: string } => ({
					namePrefix,
				})),
				// Chromium rejects a request that carries both, and the plugin
				// sets this from its own (narrower) filter list.
				acceptAllDevices: false,
			});
		try {
			return await run();
		} finally {
			bluetooth.requestDevice = original;
		}
	}

	// -----------------------------------------------------------------------
	// Connection
	// -----------------------------------------------------------------------

	/**
	 * Connect and resolve where the configuration characteristics live.
	 *
	 * The unit's primary-service layout is not documented, so rather than
	 * trusting an assumed GATT table we enumerate the live connection and
	 * find each characteristic's containing service (the same
	 * discover-don't-assume approach that ReceiptPrinterService needed).
	 */
	public async connect(deviceId: string): Promise<void> {
		await this._initialize();
		if (this._connectedDeviceId === deviceId && this._profile != null) {
			// Trusting our own bookkeeping is not safe: `onDisconnect` is the
			// only thing that clears it, and it does not always arrive (a unit
			// dropping while the page reloads, or CoreBluetooth holding a dead
			// handle — the exact failure ReceiptPrinterService documented). Ask
			// the OS before believing the link is up.
			if (await this._isReallyConnected(deviceId)) {
				return;
			}
			this._connectedDeviceId = null;
			this._profile = null;
			this._emit();
		}
		if (
			this._connectedDeviceId != null &&
			this._connectedDeviceId !== deviceId
		) {
			await this.disconnect();
		}

		// Re-register the peripheral with the plugin (its in-memory device map
		// is empty after a page reload). On NATIVE, also drop any dead handle
		// CoreBluetooth may still hold before asking for a new one (the
		// printer's field failure). On WEB this pre-disconnect is harmful:
		// Chromium answering a rapid disconnect→connect resolves the connect
		// but leaves a half-dead GATT whose service discovery then throws
		// "GATT Server is disconnected" — so there we connect over whatever
		// state exists and let the browser reuse or rebuild it.
		await this._ensureDeviceKnown(deviceId);
		if (Platform.isNative()) {
			await this._safeDisconnect(deviceId);
		}

		try {
			await BleClient.connect(deviceId, (disconnectedId) => {
				if (this._connectedDeviceId === disconnectedId) {
					this._connectedDeviceId = null;
					this._profile = null;
					this._emit();
				}
			});
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'Could not reach the sensor unit.';
			// The plugin's device map is in-memory, so a page reload (or a dev
			// rebuild) loses the grant even though the browser still lists the
			// unit as paired. Retrying the connect can never fix that — say what
			// actually will.
			if (/device not found/i.test(message)) {
				throw new SensorBleError(
					'unreachable',
					'This browser session no longer has access to the unit. Choose it again with "Connect a unit".',
				);
			}
			throw new SensorBleError('unreachable', message);
		}

		let profile: ResolvedSensorProfile | null = null;
		try {
			// Web: give Chromium a beat between gatt.connect() resolving and
			// service discovery — immediately after a reconnect the link can
			// still be settling, and getCharacteristics on it throws "GATT
			// Server is disconnected" even though connect() succeeded.
			if (!Platform.isNative()) {
				await sleep(WEB_CONNECT_SETTLE_MS);
			}
			const services = await BleClient.getServices(deviceId);
			const containing = (characteristic: string): string | null => {
				for (const service of services) {
					if (
						service.characteristics.some(
							(c) => c.uuid.toLowerCase() === characteristic,
						)
					) {
						return service.uuid;
					}
				}
				return null;
			};
			const identityService = containing(SENSOR_CHARACTERISTICS.identity);
			const settingsService = containing(SENSOR_CHARACTERISTICS.settings);
			const commandService = containing(SENSOR_CHARACTERISTICS.command);
			const statusService = containing(SENSOR_CHARACTERISTICS.status);
			const rangingService = containing(SENSOR_CHARACTERISTICS.ranging);
			if (
				identityService != null &&
				settingsService != null &&
				commandService != null
			) {
				profile = {
					identityService,
					settingsService,
					commandService,
					statusService,
					rangingService,
				};
			}
		} catch (error) {
			await this._safeDisconnect(deviceId);
			throw new SensorBleError(
				'unreachable',
				error instanceof Error
					? error.message
					: 'Could not read the sensor services.',
			);
		}

		if (profile == null) {
			await this._safeDisconnect(deviceId);
			throw new SensorBleError(
				'unsupported-device',
				'This device does not expose the sensor configuration profile.',
			);
		}

		this._connectedDeviceId = deviceId;
		this._profile = profile;
		this._emit();
	}

	public async disconnect(): Promise<void> {
		const deviceId = this._connectedDeviceId;
		if (deviceId != null) {
			await this._safeDisconnect(deviceId);
		}
		this._connectedDeviceId = null;
		this._profile = null;
		this._emit();
	}

	/**
	 * Wait for a unit to come back and reconnect to it — used after a reset,
	 * which drops the link while the unit reboots.
	 *
	 * No chooser is needed: the browser keeps the device permission for the
	 * page session (and the native layer keeps the peripheral), so we can
	 * simply retry the connection until the unit answers again. Each attempt
	 * uses a short connect timeout so a poll cycle stays responsive, and the
	 * whole wait is bounded.
	 *
	 * Returns true once connected, false if the deadline passes. Pass
	 * `shouldCancel` to abandon the wait (e.g. the operator navigated away).
	 */
	public async waitForUnit(
		deviceId: string,
		options?: {
			timeoutMs?: number;
			attemptTimeoutMs?: number;
			retryDelayMs?: number;
			onAttempt?: (attempt: number, elapsedMs: number) => void;
			shouldCancel?: () => boolean;
		},
	): Promise<boolean> {
		// These units reboot in roughly 10-20 seconds, so poll briskly: a short
		// connect timeout and a short gap detect the unit within a cycle or
		// two of it coming back, while the overall bound stays generous.
		const timeoutMs = options?.timeoutMs ?? 120_000;
		const attemptTimeoutMs = options?.attemptTimeoutMs ?? 3_000;
		const retryDelayMs = options?.retryDelayMs ?? 1_500;
		const startedAt = Date.now();

		// The peripheral we held is gone — drop it so each attempt is a fresh
		// connect rather than a reuse of a dead handle.
		this._connectedDeviceId = null;
		this._profile = null;
		this._emit();

		let attempt = 0;
		while (Date.now() - startedAt < timeoutMs) {
			if (options?.shouldCancel?.() === true) {
				return false;
			}
			attempt += 1;
			options?.onAttempt?.(attempt, Date.now() - startedAt);
			try {
				await this._initialize();
				await this._ensureDeviceKnown(deviceId);
				await BleClient.connect(
					deviceId,
					(disconnectedId) => {
						if (this._connectedDeviceId === disconnectedId) {
							this._connectedDeviceId = null;
							this._profile = null;
							this._emit();
						}
					},
					{ timeout: attemptTimeoutMs },
				);
				// Connected — resolve the profile through the normal path so
				// the service ends up in the same state as a manual connect.
				await this._safeDisconnect(deviceId);
				await this.connect(deviceId);
				return true;
			} catch {
				// Still rebooting / out of range — wait and try again.
				await sleep(retryDelayMs);
			}
		}
		return false;
	}

	private async _safeDisconnect(deviceId: string): Promise<void> {
		try {
			await BleClient.disconnect(deviceId);
		} catch {
			// Already gone.
		}
	}

	/**
	 * Ask the OS whether the link is genuinely up rather than trusting the
	 * service's own flag. A throw is treated as "not connected" — a fresh
	 * reconnect costs a second, a wrongly assumed link costs a failed write
	 * in front of the unit.
	 */
	private async _isReallyConnected(deviceId: string): Promise<boolean> {
		try {
			const connected = await BleClient.getConnectedDevices([]);
			return connected.some((device) => device.deviceId === deviceId);
		} catch {
			return false;
		}
	}

	/**
	 * Make the plugin aware of the peripheral again. Its device map is
	 * in-memory only, so after a page reload `connect()` rejects with
	 * "Device not found" no matter how close the unit is (the trap
	 * ReceiptPrinterService hit after every app restart). `getDevices`
	 * re-registers previously granted peripherals without a scan or a
	 * chooser; when even that cannot resolve it, the connect error below
	 * tells the operator to pick the unit again.
	 */
	private async _ensureDeviceKnown(deviceId: string): Promise<void> {
		try {
			const known = await BleClient.getDevices([deviceId]);
			if (known.some((device) => device.deviceId === deviceId)) {
				return;
			}
		} catch {
			// Fall through — connect() will surface the actionable error.
		}
	}

	private async _requireProfile(
		deviceId: string,
	): Promise<ResolvedSensorProfile> {
		await this.connect(deviceId);
		const profile = this._profile;
		if (profile == null || this._connectedDeviceId !== deviceId) {
			throw new SensorBleError(
				'unreachable',
				'The sensor unit is not connected.',
			);
		}
		return profile;
	}

	// -----------------------------------------------------------------------
	// Reading
	// -----------------------------------------------------------------------

	/**
	 * Connect, read identity + settings, and (by default) disconnect so the
	 * radio is free for the next unit in a bulk read. Pass
	 * `keepConnection: true` when a config-write will follow immediately.
	 */
	public async readUnit(
		deviceId: string,
		options?: { keepConnection?: boolean },
	): Promise<SensorUnitReadModel> {
		try {
			return await this._withReconnectRetry(deviceId, async () => {
				const profile = await this._requireProfile(deviceId);
				const identityText = await this._readText(
					deviceId,
					profile.identityService,
					SENSOR_CHARACTERISTICS.identity,
				);
				const settingsText = await this._readText(
					deviceId,
					profile.settingsService,
					SENSOR_CHARACTERISTICS.settings,
				);
				const identity = parseSensorIdentity(identityText);
				const settings = parseSensorSettings(settingsText);
				// An empty read is a FAILED read, not a unit with no data: the
				// characteristics can come back blank when the link is not
				// fully settled (seen right after a reconnect). Throwing here
				// routes it into the retry-after-reconnect path instead of
				// rendering a card full of dashes as though it succeeded.
				if (identity.serialNumber == null && settings.raw.trim() === '') {
					throw new SensorBleError(
						'read-failed',
						'The unit returned an empty response.',
					);
				}
				// Diagnostic block: a failure here must not fail the read.
				let status: SensorStatusModel | null = null;
				if (profile.statusService != null) {
					try {
						status = parseSensorStatus(
							await this._readText(
								deviceId,
								profile.statusService,
								SENSOR_CHARACTERISTICS.status,
							),
						);
					} catch {
						status = null;
					}
				}
				// PD-16068 — informational like status: a failure leaves the rest
				// of the read intact and the screen shows a short notice.
				let ranging: SensorRangingModel | null = null;
				if (profile.rangingService != null) {
					try {
						ranging = parseSensorRanging(
							await this._readText(
								deviceId,
								profile.rangingService,
								SENSOR_CHARACTERISTICS.ranging,
							),
						);
					} catch {
						ranging = null;
					}
				}
				return {
					deviceId,
					identity,
					settings,
					status,
					ranging,
					readAt: new Date(),
				};
			});
		} finally {
			if (options?.keepConnection !== true) {
				await this.disconnect();
			}
		}
	}

	/**
	 * Re-read just the settings block — used to confirm a write after the
	 * ~2s settle (`SETTINGS_SETTLE_MS`). Requires the caller to have kept the
	 * connection open (or reconnects transparently).
	 */
	public async readSettings(deviceId: string): Promise<SensorSettingsModel> {
		return this._withReconnectRetry(deviceId, async () => {
			const profile = await this._requireProfile(deviceId);
			const text = await this._readText(
				deviceId,
				profile.settingsService,
				SENSOR_CHARACTERISTICS.settings,
			);
			return parseSensorSettings(text);
		});
	}

	/**
	 * Run a link operation, and on failure force a fresh reconnect and retry
	 * it ONCE — the printer's Print-button behavior. A unit dropping mid-
	 * session (or a page reload emptying the plugin's device map) otherwise
	 * turns every subsequent action into "out of range" until the operator
	 * manually re-picks the unit. Settings writes are same-value idempotent,
	 * so a retried write that actually landed the first time is harmless.
	 */
	private async _withReconnectRetry<T>(
		deviceId: string,
		operation: () => Promise<T>,
	): Promise<T> {
		// PD-15776 — every attempt races the operation ceiling so a hung GATT
		// call (which otherwise never settles) becomes a normal retryable
		// failure instead of freezing the caller's UI state forever.
		try {
			return await withBleTimeout(operation(), 'read-failed', 'The operation');
		} catch {
			// The link may have died without an onDisconnect. Reset our
			// bookkeeping so connect() runs the full re-registration path,
			// then retry once; a second failure surfaces to the caller.
			this._connectedDeviceId = null;
			this._profile = null;
			this._emit();
			await withBleTimeout(
				this.connect(deviceId),
				'unreachable',
				'Reconnecting',
			);
			return withBleTimeout(operation(), 'read-failed', 'The operation');
		}
	}

	private async _readText(
		deviceId: string,
		service: string,
		characteristic: string,
	): Promise<string> {
		try {
			const value = await BleClient.read(deviceId, service, characteristic);
			return new TextDecoder().decode(value);
		} catch (error) {
			throw new SensorBleError(
				'read-failed',
				error instanceof Error
					? error.message
					: 'Could not read from the sensor unit.',
			);
		}
	}

	// -----------------------------------------------------------------------
	// Writing
	// -----------------------------------------------------------------------

	/**
	 * Write one settings command (`upp` / `LD_n` / `url` / `api_key` /
	 * `apn_name`) and return the classified device response. The device's own
	 * serial-derived id (the `deviceId` FIELD of the command, from the
	 * advertised-name code) is `commandDeviceId`; the first parameter remains
	 * the OS peripheral id used for transport.
	 *
	 * The connection is left open — callers pushing several settings in a row
	 * should `disconnect()` when done.
	 */
	public async sendCommand(
		deviceId: string,
		commandDeviceId: string,
		code: SensorCommandCode,
		value: string | number,
	): Promise<SensorEchoClassification> {
		const command = buildSensorCommand(commandDeviceId, code, value);
		return this._writeAndClassify(
			deviceId,
			commandDeviceId,
			code,
			String(value),
			command,
		);
	}

	/**
	 * Write an action command WITHOUT waiting for an acknowledgment.
	 *
	 * For a reset there is nothing to wait for: the unit tears down its
	 * Bluetooth stack as it reboots, so the echo read runs against a dying
	 * link and only delays the operator by several seconds before failing.
	 * The vendor tool does not verify this command either — it writes and
	 * moves on.
	 */
	public async sendActionFireAndForget(
		deviceId: string,
		commandDeviceId: string,
		code: SensorCommandCode,
	): Promise<void> {
		const command = buildSensorAction(commandDeviceId, code);
		const profile = await this._requireProfile(deviceId);
		const bytes = encodeSensorCommand(command);
		const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
		try {
			// PD-15776 — same hang ceiling as the retry path: a write that
			// never settles must not freeze the caller.
			await withBleTimeout(
				BleClient.write(
					deviceId,
					profile.commandService,
					SENSOR_CHARACTERISTICS.command,
					view,
				),
				'write-failed',
				'The write',
			);
		} catch (error) {
			// A link that dies mid-write is the expected shape of a reboot
			// taking effect; the caller decides what that means.
			throw new SensorBleError(
				'write-failed',
				error instanceof Error
					? error.message
					: 'Could not write to the sensor unit.',
			);
		}
	}

	/**
	 * Write an action command (reset / addressing). The codec marks these
	 * destructive; the UI must gate them behind an explicit re-commission
	 * affordance (spec.md §6.5) — this method does not add its own guard so
	 * that the policy lives in one place.
	 */
	public async sendAction(
		deviceId: string,
		commandDeviceId: string,
		code: SensorCommandCode,
	): Promise<SensorEchoClassification> {
		const command = buildSensorAction(commandDeviceId, code);
		const keyword = getSensorCommandDef(code)?.keyword ?? '';
		return this._writeAndClassify(
			deviceId,
			commandDeviceId,
			code,
			keyword,
			command,
		);
	}

	private async _writeAndClassify(
		deviceId: string,
		commandDeviceId: string,
		code: SensorCommandCode,
		value: string,
		command: string,
	): Promise<SensorEchoClassification> {
		return this._withReconnectRetry(deviceId, async () => {
			const profile = await this._requireProfile(deviceId);
			const bytes = encodeSensorCommand(command);
			const view = new DataView(
				bytes.buffer,
				bytes.byteOffset,
				bytes.byteLength,
			);
			try {
				await BleClient.write(
					deviceId,
					profile.commandService,
					SENSOR_CHARACTERISTICS.command,
					view,
				);
			} catch (error) {
				throw new SensorBleError(
					'write-failed',
					error instanceof Error
						? error.message
						: 'Could not write to the sensor unit.',
				);
			}
			// `sentBody` is the command without its CRLF terminator.
			const sentBody = command.replace(/\r\n$/, '');
			await sleep(ECHO_READ_DELAY_MS);
			const echo = await this._readText(
				deviceId,
				profile.commandService,
				SENSOR_CHARACTERISTICS.command,
			);
			const first = classifySensorEcho(
				echo,
				commandDeviceId,
				code,
				value,
				sentBody,
			);
			if (first.parsed) {
				return first;
			}
			// Action commands (mode changes) can acknowledge slower than
			// settings writes — the buffer may still hold our bytes at the
			// first read. Give the firmware another beat and look again
			// before declaring the command unacknowledged.
			await sleep(ECHO_RETRY_DELAY_MS);
			const secondEcho = await this._readText(
				deviceId,
				profile.commandService,
				SENSOR_CHARACTERISTICS.command,
			);
			return classifySensorEcho(
				secondEcho,
				commandDeviceId,
				code,
				value,
				sentBody,
			);
		});
	}
}

/**
 * The mock fleet (SensorConfigMock) takes over when the dev/test flag is
 * set in localStorage — physical units are only in Bluetooth range during
 * install visits, so this is how the provisioning flow gets verified
 * off-site. Never active without the explicit flag.
 */
function resolveSensorConfigService():
	| SensorConfigServiceImpl
	| MockSensorConfigService {
	try {
		const fleet = readMockFleetConfig();
		if (fleet != null) {
			console.debug(
				'[SensorConfigService] BLE MOCK MODE — in-memory fleet of',
				fleet.length,
			);
			return new MockSensorConfigService(fleet);
		}
	} catch {
		// localStorage unavailable (SSR/tests) — use the real transport.
	}
	return new SensorConfigServiceImpl();
}

export const SensorConfigService = resolveSensorConfigService();
